/**
 * Ugovori jednog klijenta - izdavanje, potpis, raskid, preuzimanje.
 *
 * Šablon je platformski i piše se na stranici „Pravni dokumenti"; ovde se iz
 * njega **izdaje** ugovor konkretnom klijentu. Pri izdavanju se prepisuju tekst,
 * podaci firme i spisak modula sa cenama, pa se ugovor posle ne menja kad se
 * promeni cenovnik (ADR-042, ADR-015).
 *
 * Nepopunjena polja ne zaustavljaju izdavanje nego se ističu: klijentu kome fali
 * matični broj ugovor i dalje treba, a rupa se vidi i u tekstu (`{{klijent.mb}}`)
 * i u upozorenju iznad njega.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Ban, Download, FilePlus2, FileSignature, RefreshCw, X } from 'lucide-react';
import { api } from '../../../api/client';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';

type Contract = {
  id: string;
  number: string;
  title: string;
  status: 'active' | 'terminated';
  validFrom: string;
  validUntil: string | null;
  signedAt: string | null;
  terminatedAt: string | null;
  terminationReason: string;
  templateVersion: number | null;
  createdAt: string;
};

type ContractFull = Contract & {
  body: string;
  party: Record<string, any>;
  modules: Array<Record<string, any>>;
  notes: string;
  issuedByEmail: string;
  missing?: string[];
};

function datum(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('sr-RS');
}

/** `YYYY-MM-DD` za `<input type="date">`. */
function danas(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ContractsTab({ tenantId }: { tenantId: string }) {
  const [spisak, setSpisak] = useState<Contract[]>([]);
  const [otvoren, setOtvoren] = useState<ContractFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);

  const [izdavanje, setIzdavanje] = useState(false);
  const [forma, setForma] = useState<{ validFrom: string; validUntil: string; signedAt: string; number: string; notes: string } | null>(null);
  const [raskid, setRaskid] = useState<{ ugovor: Contract; razlog: string } | null>(null);
  const [salje, setSalje] = useState(false);
  useEscapeToClose(() => { if (!salje) setForma(null); }, !!forma);
  useEscapeToClose(() => { if (!salje) setRaskid(null); }, !!raskid);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ contracts: Contract[] }>(`/admin/tenants/${tenantId}/contracts`);
      setSpisak(r.contracts ?? []);
    } catch (e: any) {
      setError(e?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const izdaj = async () => {
    if (!forma || salje) return;
    setSalje(true);
    setError(null);
    setPoruka(null);
    try {
      const u = await api.post<ContractFull>(`/admin/tenants/${tenantId}/contracts`, {
        validFrom: forma.validFrom || undefined,
        validUntil: forma.validUntil || null,
        signedAt: forma.signedAt || null,
        number: forma.number.trim() || undefined,
        notes: forma.notes.trim(),
      });
      setForma(null);
      setOtvoren(u);
      setPoruka(
        u.missing?.length
          ? `Ugovor ${u.number} je izdat, ali ${u.missing.length} polja nije popunjeno: ${u.missing.join(', ')}. Dopunite podatke klijenta pa izdajte ponovo.`
          : `Ugovor ${u.number} je izdat.`,
      );
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Izdavanje nije uspelo');
    } finally {
      setSalje(false);
    }
  };

  const otvori = async (id: string) => {
    setError(null);
    try {
      setOtvoren(await api.get<ContractFull>(`/admin/tenants/${tenantId}/contracts/${id}`));
    } catch (e: any) {
      setError(e?.message || 'Ugovor nije učitan');
    }
  };

  /**
   * PDF ide kroz `fetch`, ne kroz `api` klijenta: klijent parsira JSON, a ovde
   * stiže binarni dokument. Token se dodaje ručno, isto kao što ga dodaje `api`.
   */
  const preuzmi = async (c: Contract) => {
    setError(null);
    try {
      const token = localStorage.getItem('admin-token');
      const res = await fetch(`/api/admin/tenants/${tenantId}/contracts/${c.id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`Preuzimanje nije uspelo (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ugovor-${c.number.replace(/\//g, '-')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'Preuzimanje nije uspelo');
    }
  };

  const potpisi = async (c: Contract) => {
    setError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/contracts/${c.id}/sign`, { signedAt: new Date().toISOString() });
      setPoruka(`Ugovor ${c.number} je označen kao potpisan.`);
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Upis potpisa nije uspeo');
    }
  };

  const raskini = async () => {
    if (!raskid || salje) return;
    if (!raskid.razlog.trim()) {
      setError('Razlog raskida je obavezan.');
      return;
    }
    setSalje(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/contracts/${raskid.ugovor.id}/terminate`, {
        reason: raskid.razlog.trim(),
      });
      setPoruka(`Ugovor ${raskid.ugovor.number} je raskinut.`);
      setRaskid(null);
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Raskid nije upisan');
    } finally {
      setSalje(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text-strong)' }}>Ugovori</h3>
        <button onClick={() => void refresh()} style={btnGhost}><RefreshCw size={13} /> Osveži</button>
        <button
          onClick={() => setForma({ validFrom: danas(), validUntil: '', signedAt: '', number: '', notes: '' })}
          style={{ ...btnPrimary, marginLeft: 'auto' }}
        >
          <FilePlus2 size={13} /> Napravi ugovor
        </button>
      </div>

      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--color-text-muted)' }}>
        Ugovor se pravi iz važećeg šablona i podataka klijenta u trenutku izdavanja. Kasnija
        izmena cenovnika ili šablona <strong>ne menja</strong> već izdat ugovor.
      </p>

      {error && <div style={upozorenje}><AlertTriangle size={14} /> {error}</div>}
      {poruka && <div style={obavestenje}>{poruka}</div>}
      {loading && <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Učitavanje…</p>}

      {!loading && spisak.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
          Klijent nema nijedan ugovor.
        </p>
      )}

      {spisak.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Broj', 'Važi', 'Potpisan', 'Status', ''].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {spisak.map((c) => (
              <Fragment key={c.id}>
                <tr>
                  <td style={td}>
                    <strong style={{ color: 'var(--color-text-strong)' }}>{c.number}</strong>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                      šablon v{c.templateVersion ?? '?'}
                    </div>
                  </td>
                  <td style={td}>
                    {datum(c.validFrom)} - {c.validUntil ? datum(c.validUntil) : 'na neodređeno'}
                  </td>
                  <td style={td}>{c.signedAt ? datum(c.signedAt) : <span style={{ color: 'var(--color-text-muted)' }}>nije</span>}</td>
                  <td style={td}>
                    {c.status === 'active' ? (
                      <span style={{ ...znacka, background: 'rgba(22,163,74,0.12)', color: '#15803d' }}>aktivan</span>
                    ) : (
                      <span style={{ ...znacka, background: 'rgba(220,38,38,0.10)', color: '#b91c1c' }}>
                        raskinut {datum(c.terminatedAt)}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => void otvori(c.id)} style={btnGhost}>
                      {otvoren?.id === c.id ? 'Sakrij' : 'Pogledaj'}
                    </button>
                    <button onClick={() => void preuzmi(c)} style={{ ...btnGhost, marginLeft: 6 }}>
                      <Download size={13} /> PDF
                    </button>
                    {c.status === 'active' && !c.signedAt && (
                      <button onClick={() => void potpisi(c)} style={{ ...btnGhost, marginLeft: 6 }}>
                        <FileSignature size={13} /> Potpisan
                      </button>
                    )}
                    {c.status === 'active' && (
                      <button onClick={() => setRaskid({ ugovor: c, razlog: '' })} style={{ ...btnDanger, marginLeft: 6 }}>
                        <Ban size={13} /> Raskini
                      </button>
                    )}
                  </td>
                </tr>

                {otvoren?.id === c.id && (
                  <tr>
                    <td colSpan={5} style={{ ...td, background: 'var(--color-bg)' }}>
                      {otvoren.missing && otvoren.missing.length > 0 && (
                        <div style={{ ...upozorenje, marginBottom: 10 }}>
                          <AlertTriangle size={14} />
                          Nepopunjena polja: {otvoren.missing.join(', ')}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                        Izdao {otvoren.issuedByEmail || '-'} · {otvoren.modules.length} modula u prepisu
                        {otvoren.notes ? ` · ${otvoren.notes}` : ''}
                      </div>
                      <pre
                        style={{
                          margin: 0, maxHeight: 380, overflow: 'auto',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                          fontSize: 12, lineHeight: 1.5, color: 'var(--color-text-strong)',
                        }}
                      >
                        {otvoren.body}
                      </pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {forma && (
        <div style={overlay}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <FilePlus2 size={16} />
              <strong style={{ color: 'var(--color-text-strong)' }}>Napravi ugovor</strong>
              <button onClick={() => setForma(null)} style={{ ...btnGhost, marginLeft: 'auto' }}><X size={14} /></button>
            </header>

            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-text-muted)' }}>
              Tekst, podaci klijenta i spisak modula sa cenama prepisuju se u ugovor <strong>sada</strong>
              {' '}i posle se ne menjaju.
            </p>

            <label style={oznaka}>Važi od</label>
            <input type="date" value={forma.validFrom} onChange={(e) => setForma({ ...forma, validFrom: e.target.value })} style={polje} />

            <label style={oznaka}>Važi do (prazno = na neodređeno vreme)</label>
            <input type="date" value={forma.validUntil} onChange={(e) => setForma({ ...forma, validUntil: e.target.value })} style={polje} />

            <label style={oznaka}>Datum potpisa (ako je već potpisan)</label>
            <input type="date" value={forma.signedAt} onChange={(e) => setForma({ ...forma, signedAt: e.target.value })} style={polje} />

            <label style={oznaka}>Broj (prazno = dodeljuje se sam)</label>
            <input
              value={forma.number}
              onChange={(e) => setForma({ ...forma, number: e.target.value })}
              placeholder="npr. 2026/0007 - za prepis ugovora zaključenog van sistema"
              style={polje}
            />

            <label style={oznaka}>Interna napomena (klijent je ne vidi)</label>
            <input value={forma.notes} onChange={(e) => setForma({ ...forma, notes: e.target.value })} style={polje} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              <button onClick={() => setForma(null)} style={{ ...btnGhost, width: '100%', textAlign: 'center' }} disabled={salje}>Otkaži</button>
              <button onClick={() => void izdaj()} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }} disabled={salje}>
                {salje ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {raskid && (
        <div style={overlay}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Ban size={16} />
              <strong style={{ color: 'var(--color-text-strong)' }}>Raskid ugovora {raskid.ugovor.number}</strong>
              <button onClick={() => setRaskid(null)} style={{ ...btnGhost, marginLeft: 'auto' }}><X size={14} /></button>
            </header>

            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--color-text-muted)' }}>
              Ugovor se <strong>ne briše</strong> - ostaje kao dokaz šta je važilo do danas. Klijent će ga
              i dalje videti, sa oznakom da je raskinut.
            </p>

            <label style={oznaka}>Razlog (obavezno)</label>
            <input
              value={raskid.razlog}
              onChange={(e) => setRaskid({ ...raskid, razlog: e.target.value })}
              placeholder="npr. otkaz klijenta sa 30 dana"
              style={polje}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              <button onClick={() => setRaskid(null)} style={{ ...btnGhost, width: '100%', textAlign: 'center' }} disabled={salje}>Otkaži</button>
              <button onClick={() => void raskini()} style={{ ...btnDanger, width: '100%', justifyContent: 'center' }} disabled={salje}>
                {salje ? 'Upisujem…' : 'Raskini'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border)',
};

const td: React.CSSProperties = {
  padding: '10px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top',
};

const znacka: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 8px', borderRadius: 999, fontSize: 12, whiteSpace: 'nowrap',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'transparent',
  color: 'var(--color-text-muted)',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', fontWeight: 600,
};

const btnDanger: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid #ef4444', background: 'rgba(239,68,68,0.08)', color: '#b91c1c',
};

const upozorenje: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 12px', borderRadius: 6, marginBottom: 12,
  background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 13,
};

const obavestenje: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 6, marginBottom: 12,
  background: 'rgba(22,163,74,0.10)', color: '#15803d', fontSize: 13,
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20,
};

const modal: React.CSSProperties = {
  width: '100%', maxWidth: 480, borderRadius: 10, padding: 20,
  background: 'var(--color-bg-surface, #fff)', border: '1px solid var(--color-border)',
  maxHeight: '90vh', overflowY: 'auto',
};

const oznaka: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, margin: '10px 0 4px',
  color: 'var(--color-text-strong)',
};

const polje: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--color-border)', background: 'var(--color-bg-surface, #fff)',
  color: 'var(--color-text-strong)', boxSizing: 'border-box',
};
